/**
 * Grouped target-field picker shared by the mapping table and the pattern editor
 * dialog for the transform-aware column mapping screen (custom-supplier-import-
 * transforms). Mirrors the group-picker used by MappingPhase's field select
 * (mapping-phase-table.tsx) but as a standalone, reusable presentational piece.
 */
import * as React from 'react';
import type { FieldOptionItem } from '@ooh/contracts';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@ooh/ui/components/select';
import { GROUP_ORDER, type FieldGroupKey } from './import-field-group-order';

// Radix disallows '' as a SelectItem value, so the "skip" option uses a sentinel
// that's translated back to null at the boundary. Never leaks outside this file.
const SKIP_VALUE = '__skip__';

export interface TargetFieldSelectLabels {
  skipLabel: string;
  groupGeneral: string;
  groupPhysical: string;
  groupMuralFlags: string;
  groupDigital: string;
  groupInternalCost: string;
  groupClientPrice: string;
}

function getGroupLabel(group: FieldGroupKey, labels: TargetFieldSelectLabels): string {
  const map: Record<FieldGroupKey, string> = {
    general: labels.groupGeneral,
    physical: labels.groupPhysical,
    mural_flags: labels.groupMuralFlags,
    digital: labels.groupDigital,
    internal_cost: labels.groupInternalCost,
    client_price: labels.groupClientPrice,
  };
  return map[group];
}

/** Groups a flat FieldOptionItem list by GROUP_ORDER, dropping empty groups — shared by
 * TargetFieldSelect (below) and any other target-field picker (e.g. MappingFieldPicker via
 * PatternEditorDialog) that needs the same pre-grouped shape instead of duplicating this. */
export function buildFieldOptionGroups(
  fieldOptions: FieldOptionItem[],
  labels: TargetFieldSelectLabels,
): { groupKey: FieldGroupKey; label: string; options: FieldOptionItem[] }[] {
  return GROUP_ORDER.map((groupKey) => ({
    groupKey,
    label: getGroupLabel(groupKey, labels),
    options: fieldOptions.filter((o) => o.group === groupKey),
  })).filter((g) => g.options.length > 0);
}

export interface TargetFieldSelectProps {
  /** Selected field key, or null for "skip this column". */
  value: string | null;
  onChange: (value: string | null) => void;
  fieldOptions: FieldOptionItem[];
  labels: TargetFieldSelectLabels;
  testId?: string;
}

export function TargetFieldSelect({
  value,
  onChange,
  fieldOptions,
  labels,
  testId,
}: TargetFieldSelectProps) {
  const groupedOptions = React.useMemo(
    () => buildFieldOptionGroups(fieldOptions, labels),
    [fieldOptions, labels],
  );

  return (
    <Select
      value={value ?? SKIP_VALUE}
      onValueChange={(next) => onChange(next === SKIP_VALUE ? null : next)}
    >
      <SelectTrigger data-testid={testId}>
        <SelectValue placeholder={labels.skipLabel} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={SKIP_VALUE}>{labels.skipLabel}</SelectItem>
        {groupedOptions.map((g) => (
          <SelectGroup key={g.groupKey}>
            <SelectLabel>{g.label}</SelectLabel>
            {g.options.map((opt) => (
              <SelectItem key={opt.key} value={opt.key}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );
}
